import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, ChevronDown } from "lucide-react";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext"
import { DefaultButton } from "@/components/buttons/DefaultButton";
import { creativesService } from "../services/creatives.service";
import type { ProductInfoCreative } from "../types/creatives.types";

interface AdsCreativesHeaderProps {
  onCreate?: () => void;
  onProductFilterChange?: (productId: string | null) => void;
}

export function AdsCreativesHeader({ onCreate, onProductFilterChange }: AdsCreativesHeaderProps) {
  const { language } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [products, setProducts] = useState<ProductInfoCreative[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  
  const initialProductId = searchParams.get("product_id");
  const [selectedProductId, setSelectedProductId] = useState<string | null>(initialProductId);

  useEffect(() => {
    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        const data = await creativesService.listProducts();
        setProducts(data);
      } catch (error) {
        console.error("Error loading products:", error);
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProducts();
  }, []);

  const uniqueProducts = products
    .filter((item) => item.creatives && item.creatives.length > 0)
    .reduce((acc, item) => {
      const existingProduct = acc.find((p) => p.product_id === item.product_id);
      if (existingProduct) {
        existingProduct.creativesCount += item.creatives?.length || 0;
      } else {
        acc.push({
          product_id: item.product_id,
          name: item.name,
          image_url: item.image_url,
          creativesCount: item.creatives?.length || 0,
        });
      }
      return acc;
    }, [] as Array<{ product_id: string; name: string; image_url?: string; creativesCount: number }>);

  const selectedProduct = uniqueProducts.find((p) => p.product_id === selectedProductId);

  useEffect(() => {
    const urlProductId = searchParams.get("product_id");
    if (urlProductId !== selectedProductId) {
      setSelectedProductId(urlProductId);
    }
  }, [searchParams]);

  const handleProductSelect = (productId: string | null) => {
    setSelectedProductId(productId);
    setIsDropdownOpen(false);
    
    const newSearchParams = new URLSearchParams(searchParams);
    if (productId) {
      newSearchParams.set("product_id", productId);
      console.log("Filtro cambiado - Product ID:", productId);
    } else {
      newSearchParams.delete("product_id");
      console.log("Filtro cambiado - Todos los productos");
    }
    setSearchParams(newSearchParams);
    
    onProductFilterChange?.(productId);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center shadow-sm border border-gray-100">
            <Sparkles className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{dictionary["your-ads"]?.[language as Language] || "Tus Anuncios"}</h1>
            <p className="text-gray-500">{dictionary["select-a-product-to-create-ads"]?.[language as Language] || "Selecciona un producto para crear anuncios"}</p>
          </div>
        </div>

        <DefaultButton
          title={dictionary["create-new-ad-with-ai"]?.[language as Language] || "Crear nuevo Anuncio con IA"}
          onClick={onCreate ?? (() => console.log("crear nuevo anuncio con IA"))}
          icon={<Sparkles className="h-4 w-4" />}
          className="w-full md:w-auto font-semibold"
        />
      </div>

      {/* Product Filter */}
      <div className="relative flex justify-end">
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center gap-2 px-4 py-2 rounded-full border border-blue-500 bg-white hover:bg-gray-50 transition-colors"
        >
          <span className="text-sm text-gray-600 flex-shrink-0">Producto:</span>
          <span className="text-sm font-medium text-gray-900 truncate max-w-[200px] md:max-w-[300px]">
            {selectedProduct
              ? `${selectedProduct.name} (${selectedProduct.creativesCount})`
              : "Todos"}
          </span>
          <ChevronDown className={`h-4 w-4 text-gray-600 transition-transform flex-shrink-0 ${isDropdownOpen ? "rotate-180" : ""}`} />
        </button>

        {isDropdownOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsDropdownOpen(false)}
            />
            <div className="absolute right-0 top-full mt-2 z-30 w-64 bg-white border border-gray-200/40 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto">
              <button
                onClick={() => handleProductSelect(null)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-colors ${
                  selectedProductId === null
                    ? "bg-purple-50 text-purple-600 font-medium"
                    : "text-gray-900 hover:bg-gray-50"
                }`}
              >
                <span>Todos los productos</span>
                <span className="text-xs text-gray-500 ml-auto">
                  {uniqueProducts.reduce((sum, p) => sum + p.creativesCount, 0)}
                </span>
              </button>
              <div className="border-t border-gray-200/30"></div>
              {uniqueProducts.map((product) => (
                <button
                  key={product.product_id}
                  onClick={() => handleProductSelect(product.product_id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-colors ${
                    selectedProductId === product.product_id
                      ? "bg-purple-50 text-purple-600 font-medium"
                      : "text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  {product.image_url ? (
                    <div className="h-7 w-7 rounded-lg bg-white border border-gray-200/30 overflow-hidden flex-shrink-0">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="h-full w-full object-contain p-0.5"
                      />
                    </div>
                  ) : (
                    <div className="h-7 w-7 rounded-lg bg-gray-100 border border-gray-200/30 flex-shrink-0"></div>
                  )}
                  <span className="truncate flex-1">{product.name}</span>
                  <span className="text-xs text-gray-500">{product.creativesCount}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


